import {
  faExclamationTriangle,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import * as Config from '../../../public/external-links'
import Accordion from '../../common/Accordion'
import GreyBox from '../../common/GreyBox'
import { ICallout } from '../../../models/Callout'
import useCallout from '../../../hooks/useCallout'
import LoaderContext from '../../context/FormLoader'
import Conditional from '../../common/Conditional'
import CalloutDisplay from '../../common/CalloutDisplay'
import DocumentLink from '../../common/DocumentLink'

export const EndOfProbationHeader = ({ show, setShow, handleStart }) => {
  const mountedRef = useRef(false)
  const [data, setData] = useState<ICallout[]>([])
  const [getCallout] = useCallout('EndOfProbationTemplate', 'Before')
  const [isLoading, setIsLoading] = useState(true)
  const loader = useContext(LoaderContext)

  useEffect(() => {
    mountedRef.current = true
    loader.up()
    if (mountedRef.current) {
      getCallout()
        .then((response) => {
          if (mountedRef.current) {
            setData(response.callouts)
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setIsLoading(false)
          loader.down()
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])
  return (
    <>
      <h1>End of probation</h1>
      <Accordion
        heading="Welcome to our Template Tool"
        expanded={show}
        onClick={() => setShow()}
      >
        <p></p>
        <Conditional if={!isLoading}>
          {data &&
            data.map((c, i) => (
              <CalloutDisplay
                key={i}
                className=""
                content={c.content}
                name="EndOfProbation"
              />
            ))}
        </Conditional>
        <p></p>
        <p>
          This tool will help you prepare a letter to advise your employee that
          they have successfully completed their probation period.
        </p>
        <p>
          If your employee has not successfully passed probation, and you wish
          to end their employment, please use our template&nbsp;
          <DocumentLink
            name="Termination letter"
            urlDoc={Config.INTERACTIVE_TEMPLATE_TERMINATION_LETTER_DOCX}
            urlPdf={Config.INTERACTIVE_TEMPLATE_TERMINATION_LETTER_PDF}
            sizeDoc=" 37.6KB"
            sizePdf=" 371.9KB"
          />
          .
        </p>
        <p>
          The tool will ask you simple questions and use your answers to tailor
          a letter for you. You will be able to save and edit your letter at the
          end.
        </p>
        <h2>Important information</h2>
        <p>
          Click the&nbsp;
          <FontAwesomeIcon
            className="ml-xs"
            icon={faExclamationTriangle}
            size={'1x'}
            color={'#da291c'}
          />
          &nbsp;buttons for important information about workplace rights and
          obligations
        </p>
        <h2>Get help</h2>
        <p>
          Click the&nbsp;
          <FontAwesomeIcon
            className="ml-xs"
            icon={faInfoCircle}
            size={'lg'}
            color={'#1b365d'}
          />
          &nbsp;buttons for explanations or help answering the question
        </p>
        <p>
          To protect your privacy none of the information you enter is stored in
          this tool or captured by the Office of the Fair Work Ombudsman.
        </p>

        <GreyBox>
          <p>
            <strong>Before you begin:</strong> To prepare your letter you will
            need to know if an award or enterprise agreement applies to the
            employee, whether they received a document setting out their terms
            and conditions of employment, and the end date of the probation
            period.
          </p>
          <p>All fields are required unless marked optional.</p>
        </GreyBox>
        <Button className="ui is-primary button" onClick={handleStart}>
          Start
        </Button>

        <p>
          If you do not wish to use this tool, you can download and complete the
          following blank template letter:
        </p>
        <ul>
          <li>
            <DocumentLink
              name="Successful probation letter"
              urlDoc={
                Config.INTERACTIVE_TEMPLATE_SUCCESSFUL_PROBATION_LETTER_DOCX
              }
              urlPdf={
                Config.INTERACTIVE_TEMPLATE_SUCCESSFUL_PROBATION_LETTER_PDF
              }
              sizeDoc=" 60KB"
              sizePdf=" 126.2KB"
            />
            .
          </li>
        </ul>
      </Accordion>
    </>
  )
}
